import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Autocomplete, Box, Checkbox } from "@mui/material";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { addProduct } from "slices/Masters/product/thunk";
import { addType, fetchTypesBySubCategory } from "slices/Masters/type/thunk";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import { fetchSuCategoryBasedOnSelectedCategory } from "slices/Masters/sub-category/thunk";
import { fetchHsnCodeByDropDown } from "slices/Masters/hsn-code/thunk";
import { fetchUOMByDropDown } from "slices/Masters/unit/thunk";
import { fetchMakeByDropDown } from "slices/Masters/make/thunk";
import { addModel, fetchModelByMakeDropDown } from "slices/Masters/model/thunk";
import { autocompleteDropdownStyles } from "Constants/Style";
import { MecordColors } from "Constants/Color";

interface ProductFormValues {
  item_type: "products";
  item_name: string;
  category: string; // stores ID
  sub_category: string; // stores ID
  type: string[]; // stores array of type IDs
  make: string; // stores ID
  model: string; // stores ID
  sku: string;
  unit_price: string;
  unit: string; // stores ID
  hsn: string; // stores ID
  gst: string;
  status: boolean;
  description?: string;
  existing_image_ref?: string | null;
}

interface ProductFormModalProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ProductFormModal: React.FC<ProductFormModalProps> = (props) => {
  const { onSuccess, onCancel } = props;
  const dispatch = useDispatch<any>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [makes, setMakes] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [hsnCodes, setHsnCodes] = useState<any[]>([]);
  const [showQuickTypeModal, setShowQuickTypeModal] = useState(false);
  const [showQuickModelModal, setShowQuickModelModal] = useState(false);
  const [quickTypeName, setQuickTypeName] = useState("");
  const [quickModelName, setQuickModelName] = useState("");
  const [isQuickTypeSaving, setIsQuickTypeSaving] = useState(false);
  const [isQuickModelSaving, setIsQuickModelSaving] = useState(false);
  const prevCategoryRef = useRef<string>("");
  const prevMakeRef = useRef<string>("");

  const initialValues: ProductFormValues = {
    item_type: "products",
    item_name: "",
    category: "",
    sub_category: "",
    type: [],
    make: "",
    model: "",
    sku: "",
    unit_price: "",
    unit: "",
    hsn: "",
    gst: "",
    status: true,
    description: "",
    existing_image_ref: "",
  };

  const validationSchema = Yup.object().shape({
    item_name: Yup.string().required("Product Name is required"),
    category: Yup.string().required("Category is required"),
    sub_category: Yup.string().required("Sub-Category is required"),
    make: Yup.string().required("Make is required"),
    model: Yup.string().required("Model is required"),
    sku: Yup.string().required("SKU Code is required"),
    unit: Yup.string().required("Unit is required"),
    unit_price: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value
      )
      .typeError("Unit Price must be a number")
      .min(0, "Unit Price cannot be negative")
      .notRequired(),
    hsn: Yup.string().required("HSN Code is required"),
    gst: Yup.string()
      .required("GST % is required")
      .test("is-valid-gst", "GST % must be a valid number", (value) => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }),
  });
  function createJsonPayload(values: ProductFormValues) {
    const payload: any = {
      product_name: values.item_name,
      product_sku: values.sku,
      unit_price: Number(values.unit_price),
      category_id: values.category,
      sub_category_id: values.sub_category,
      make_id: values.make,
      model_id: values.model,
      unit_id: values.unit,
      hsn_id: values.hsn,
      // documents: values.document_urls,
      // remark: "",
    };

    if (values.type && values.type.length > 0) {
      payload.type_ids = values.type; // can send as array if server accepts it
    }

    // Optional fields
    // if (values.description) {
    //   payload.description = values.description;
    // }

    return payload;
  }

  const formik = useFormik<ProductFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // const formData = new FormData();

        // formData.append("item_type", values.item_type);
        // formData.append("product_name", values.item_name);
        // formData.append("product_sku", values.sku);
        // if (values.unit_price) formData.append("unit_price", values.unit_price);

        // if (values.category) formData.append("category_id", values.category);
        // if (values.sub_category)
        //   formData.append("sub_category_id", values.sub_category);
        // if (values.type && values.type.length > 0) {
        //   values.type.forEach((typeId) => {
        //     formData.append("type_id", typeId);
        //   });
        // }
        // if (values.make) formData.append("make", values.make);
        // if (values.model) formData.append("model", values.model);
        // if (values.unit) formData.append("unit", values.unit);
        // if (values.hsn) formData.append("hsn", values.hsn);

        // if (values.gst) formData.append("gst_percent", values.gst);
        // formData.append("status", values.status ? "1" : "0");
        // if (values.description)
        //   formData.append("description", values.description);

        // if (image) {
        //   formData.append("attachment", image);
        // }

        const formData = createJsonPayload(values);

        await dispatch(addProduct(formData)).unwrap();
        toast.success("Product added successfully");

        // If onSuccess callback is provided, call it instead of navigating
        if (onSuccess) {
          onSuccess();
        }
      } catch (err: any) {
        const duplicateMessage = err?.errors?.item_name?.[0];
        if (duplicateMessage) {
          toast.error("Product with this name already exists");
        } else {
          toast.error(err?.message || "Failed to save");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Fetch dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      const [cat, hsn, uom, make] = await Promise.all([
        dispatch(fetchCategoryByDropDown()).unwrap(),
        dispatch(fetchHsnCodeByDropDown()).unwrap(),
        dispatch(fetchUOMByDropDown()).unwrap(),
        dispatch(fetchMakeByDropDown()).unwrap(),
      ]);
      setCategories(cat || []);
      setHsnCodes(hsn?.data || []);
      setUnits(uom?.data || []);
      setMakes(make?.data || []);
    };
    loadDropdowns();
  }, [dispatch]);

  // Fetch subcategories
  useEffect(() => {
    const currentCategory = formik.values.category;
    // Reset sub_category when category changes
    if (
      prevCategoryRef.current &&
      prevCategoryRef.current !== currentCategory
    ) {
      formik.setFieldValue("sub_category", "");
    }
    prevCategoryRef.current = currentCategory;

    if (currentCategory) {
      dispatch(fetchSuCategoryBasedOnSelectedCategory(currentCategory))
        .unwrap()
        .then((res: any) => setSubCategories(res || []))
        .catch(() => setSubCategories([]));
    } else {
      setSubCategories([]);
    }
  }, [formik.values.category, dispatch]);

  // Fetch models
  useEffect(() => {
    const currentMake = formik.values.make;
    // Reset model when make changes
    if (prevMakeRef.current && prevMakeRef.current !== currentMake) {
      formik.setFieldValue("model", "");
    }
    prevMakeRef.current = currentMake;

    if (currentMake) {
      dispatch(fetchModelByMakeDropDown(currentMake))
        .unwrap()
        .then((res: any) => setModels(res?.data || []))
        .catch(() => setModels([]));
    } else {
      setModels([]);
    }
  }, [formik.values.make, dispatch]);

  // Fetch types when sub-category changes
  useEffect(() => {
    if (formik.values.sub_category) {
      dispatch(fetchTypesBySubCategory(formik.values.sub_category))
        .unwrap()
        .then((res: any) => setTypes(res?.data || []))
        .catch(() => setTypes([]));
    } else {
      setTypes([]);
      formik.setFieldValue("type", []);
    }
  }, [formik.values.sub_category, dispatch]);

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };
  const getId = (option: any, ...keys: string[]) => {
    for (const key of keys) {
      if (option?.[key]) return option[key];
    }
    return option?.id || "";
  };

  const handleQuickAddType = async () => {
    const typeName = quickTypeName.trim();

    if (!formik.values.category || !formik.values.sub_category) {
      toast.error("Please select category and sub-category first");
      return;
    }

    if (!typeName) {
      toast.error("Please enter type name");
      return;
    }

    try {
      setIsQuickTypeSaving(true);
      const result = await dispatch(
        addType({
          category_id: formik.values.category,
          sub_category_id: formik.values.sub_category,
          type_name: typeName,
        })
      ).unwrap();

      const refreshed = await dispatch(
        fetchTypesBySubCategory(formik.values.sub_category)
      ).unwrap();
      const refreshedTypes = refreshed?.data || [];
      const createdType = result?.data?.data || result?.data || result;
      const fallbackType = refreshedTypes.find(
        (type: any) =>
          type.type_name?.toLowerCase() === typeName.toLowerCase()
      );
      const createdId =
        getId(createdType, "type_id", "id") ||
        getId(fallbackType, "type_id", "id");

      setTypes(refreshedTypes);
      if (createdId && !formik.values.type.includes(createdId)) {
        formik.setFieldValue("type", [...formik.values.type, createdId]);
      }
      setQuickTypeName("");
      setShowQuickTypeModal(false);
      toast.success("Type added successfully");
    } catch (err: any) {
      toast.error(err?.message || err?.data?.message || "Failed to add type");
    } finally {
      setIsQuickTypeSaving(false);
    }
  };

  const handleQuickAddModel = async () => {
    const modelName = quickModelName.trim();

    if (!formik.values.make) {
      toast.error("Please select make first");
      return;
    }

    if (!modelName) {
      toast.error("Please enter model name");
      return;
    }

    try {
      setIsQuickModelSaving(true);
      const result = await dispatch(
        addModel({ make_id: formik.values.make, model_name: modelName })
      ).unwrap();

      const refreshed = await dispatch(
        fetchModelByMakeDropDown(formik.values.make)
      ).unwrap();
      const refreshedModels = refreshed?.data || [];
      const createdModel = result?.data?.data || result?.data || result;
      const fallbackModel = refreshedModels.find(
        (model: any) =>
          model.model_name?.toLowerCase() === modelName.toLowerCase()
      );
      const createdId =
        getId(createdModel, "model_id", "id") ||
        getId(fallbackModel, "model_id", "id");

      setModels(refreshedModels);
      if (createdId) {
        formik.setFieldValue("model", createdId);
      }
      setQuickModelName("");
      setShowQuickModelModal(false);
      toast.success("Model added successfully");
    } catch (err: any) {
      toast.error(err?.message || err?.data?.message || "Failed to add model");
    } finally {
      setIsQuickModelSaving(false);
    }
  };
  return (
    <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
      {/* Basic Details */}
      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Product Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="item_name"
            value={formik.values.item_name}
            onChange={formik.handleChange}
            placeholder="Enter Product Name"
            isInvalid={!!formik.errors.item_name && formik.touched.item_name}
            onBlur={formik.handleBlur}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.item_name}
          </Form.Control.Feedback>
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Category <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.category_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.category_id === value.category_id
            }
            value={
              categories.find(
                (c) => c.category_id === formik.values.category
              ) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue("category", newValue?.category_id || "");
              formik.setFieldValue("sub_category", ""); // Reset sub_category when category changes
            }}
            onBlur={() => {
              formik.setFieldTouched("category", true);
            }}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Category"
                size="small"
                error={!!formik.errors.category && formik.touched.category}
                helperText={
                  formik.errors.category && formik.touched.category
                    ? formik.errors.category
                    : ""
                }
              />
            )}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Sub-Category <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={subCategories}
            getOptionLabel={(option) => option.sub_category_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.sub_category_id === value.sub_category_id
            }
            value={
              subCategories.find(
                (sc) => sc.sub_category_id === formik.values.sub_category
              ) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue(
                "sub_category",
                newValue?.sub_category_id || ""
              );
              formik.setFieldValue("type", []); // Reset type when sub_category changes
            }}
            onBlur={() => {
              formik.setFieldTouched("sub_category", true);
            }}
            disabled={!formik.values.category}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Sub-Category"
                size="small"
                error={
                  !!formik.errors.sub_category && formik.touched.sub_category
                }
                helperText={
                  formik.errors.sub_category && formik.touched.sub_category
                    ? formik.errors.sub_category
                    : ""
                }
              />
            )}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={4}>
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="fw-semibold mb-0" style={commonLabelStyle}>
              Type
            </Form.Label>
            <Button
              type="button"
              size="sm"
              variant="link"
              className="p-0"
              onClick={() => setShowQuickTypeModal(true)}
            >
              + Add Type
            </Button>
          </div>
          <Autocomplete
            multiple
            options={types}
            value={types.filter((type) =>
              formik.values.type.includes(type.type_id)
            )}
            onChange={(event, selectedTypes) => {
              const selectedIds = selectedTypes.map((type) => type.type_id);
              formik.setFieldValue("type", selectedIds);
            }}
            getOptionLabel={(option) => option.type_name || ""}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: 0 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",

                    // ✅ Hover state
                    "&:hover": {
                      backgroundColor: MecordColors.Hover,
                      color: MecordColors.ActiveText,
                    },

                    // ✅ Selected state
                    ...(selected && {
                      backgroundColor: MecordColors.ActiveBackground,
                      color: MecordColors.ActiveText,
                    }),
                  }}
                >
                  {/* Option Label */}
                  <span>
                    {typeof option === "string" ? option : option.type_name}
                  </span>

                  {/* Show X only if option is selected */}
                  {selected && (
                    <span
                      style={{
                        cursor: "pointer",
                        marginLeft: "10px",
                        color: "#d32f2f",
                        fontWeight: "bold",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent normal selection toggling
                        const newValues = formik.values.type.filter(
                          (id: any) => id !== option.type_id
                        );
                        formik.setFieldValue("type", newValues);
                      }}
                    >
                      ✕
                    </span>
                  )}
                </Box>
              </li>
            )}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Types"
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
                            value.map((option: any, index: number) => {
                              const { key, ...tagProps } = getTagProps({ index });
                              return (
                                <Box
                                  key={option.type_id}
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    backgroundColor: MecordColors.ActiveBackground,
                                    borderRadius: "3px",
                                    padding: "2px 6px",
                                    margin: "1px",
                                    fontSize: "0.75rem",
                                  }}
                                  {...tagProps}
                                >
                                  {option.type_name}
                                </Box>
                              );
                            })
                          }
            disabled={!formik.values.sub_category}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Make <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={makes}
            getOptionLabel={(option) => option.make_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.make_id === value.make_id
            }
            value={makes.find((m) => m.make_id === formik.values.make) || null}
            onChange={(_, newValue) => {
              formik.setFieldValue("make", newValue?.make_id || "");
              formik.setFieldValue("model", ""); // Reset model when make changes
            }}
            onBlur={() => {
              formik.setFieldTouched("make", true);
            }}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Make"
                size="small"
                error={!!formik.errors.make && formik.touched.make}
                helperText={
                  formik.errors.make && formik.touched.make
                    ? formik.errors.make
                    : ""
                }
              />
            )}
          />
        </Col>
        <Col xs={12} md={4}>
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="fw-semibold mb-0" style={commonLabelStyle}>
              Model <span className="text-danger">*</span>
            </Form.Label>
            <Button
              type="button"
              size="sm"
              variant="link"
              className="p-0"
              onClick={() => setShowQuickModelModal(true)}
            >
              + Add Model
            </Button>
          </div>
          <Autocomplete
            options={models}
            getOptionLabel={(option) => option.model_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.model_id === value.model_id
            }
            value={
              models.find((mo) => mo.model_id === formik.values.model) || null
            }
            onChange={(_, newValue) => {
              formik.setFieldValue("model", newValue?.model_id || "");
            }}
            onBlur={() => {
              formik.setFieldTouched("model", true);
            }}
            disabled={!formik.values.make}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Model"
                size="small"
                error={!!formik.errors.model && formik.touched.model}
                helperText={
                  formik.errors.model && formik.touched.model
                    ? formik.errors.model
                    : ""
                }
              />
            )}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            SKU <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            name="sku"
            value={formik.values.sku}
            onChange={formik.handleChange}
            placeholder="Enter SKU Code"
            isInvalid={!!formik.errors.sku && formik.touched.sku}
            onBlur={formik.handleBlur}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.sku}
          </Form.Control.Feedback>
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Unit <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={units}
            getOptionLabel={(option) => option.unit_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.unit_id === value.unit_id
            }
            value={units.find((u) => u.unit_id === formik.values.unit) || null}
            onChange={(_, newValue) => {
              formik.setFieldValue("unit", newValue?.unit_id || "");
            }}
            onBlur={() => {
              formik.setFieldTouched("unit", true);
            }}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Unit"
                size="small"
                error={!!formik.errors.unit && formik.touched.unit}
                helperText={
                  formik.errors.unit && formik.touched.unit
                    ? formik.errors.unit
                    : ""
                }
              />
            )}
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Unit Price
          </Form.Label>
          <Form.Control
            name="unit_price"
            value={formik.values.unit_price}
            onChange={formik.handleChange}
            placeholder="Enter Unit Price"
            onBlur={formik.handleBlur}
            isInvalid={!!formik.errors.unit_price && formik.touched.unit_price}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.unit_price}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* Additional Information Section */}
      <div className="mt-4">
        <h5 className="mb-3">Additional Information</h5>
        <Row className="mb-3">
          <Col xs={12} md={6}>
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              HSN Code <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={hsnCodes}
              getOptionLabel={(option) => option.hsn_code || ""}
              isOptionEqualToValue={(option, value) =>
                option.hsn_code_id === value.hsn_code_id
              }
              value={
                hsnCodes.find((h) => h.hsn_code_id === formik.values.hsn) ||
                null
              }
              onChange={(_, newValue) => {
                formik.setFieldValue("hsn", newValue?.hsn_code_id || "");
                // Auto-fill GST from HSN code
                if (newValue) {
                  formik.setFieldValue(
                    "gst",
                    newValue.gst.gst_percentage.toString()
                  );
                } else {
                  formik.setFieldValue("gst", "");
                }
              }}
              onBlur={() => {
                formik.setFieldTouched("hsn", true);
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select HSN Code"
                  size="small"
                  error={!!formik.errors.hsn && formik.touched.hsn}
                  helperText={
                    formik.errors.hsn && formik.touched.hsn
                      ? formik.errors.hsn
                      : ""
                  }
                />
              )}
            />
          </Col>
          <Col xs={12} md={6}>
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              GST % <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="gst"
              value={formik.values.gst}
              placeholder="GST % (Auto-filled from HSN)"
              readOnly
              isInvalid={!!formik.errors.gst && formik.touched.gst}
              style={commonInputStyle}
            />
            {formik.errors.gst && formik.touched.gst && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {formik.errors.gst}
              </Form.Control.Feedback>
            )}
          </Col>
        </Row>
        {/* Upload Image */}
        {/* <Row>
          <Col xs={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Upload Image
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const target = e.target as HTMLInputElement;
                  const file = target.files?.[0] || null;
                  setImage(file);
                  if (file) setExistingImageUrl(null);
                }}
                style={commonInputStyle}
              />

            
              {image && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="preview"
                    className="img-thumbnail"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}

             
              {!image && existingImageUrl && (
                <div className="mt-2">
                  <img
                    src={existingImageUrl}
                    alt="existing"
                    className="img-thumbnail"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row> */}
      </div>


      <style>
        {`
          .bright-white-close .btn-close {
            filter: invert(1) brightness(2);
            opacity: 1;
          }
        `}
      </style>
      <Modal
        show={showQuickTypeModal}
        onHide={() => !isQuickTypeSaving && setShowQuickTypeModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bright-white-close"
          style={{
            backgroundColor: MecordColors.ThemeBGColor,
            paddingBottom: "12px",
          }}
        >
          <Modal.Title style={{ color: "#fff" }}>Add Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Category
              </Form.Label>
              <Form.Control
                value={
                  categories.find(
                    (category) => category.category_id === formik.values.category
                  )?.category_name || ""
                }
                readOnly
                style={commonInputStyle}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Sub-Category
              </Form.Label>
              <Form.Control
                value={
                  subCategories.find(
                    (subCategory) =>
                      subCategory.sub_category_id === formik.values.sub_category
                  )?.sub_category_name || ""
                }
                readOnly
                style={commonInputStyle}
              />
            </Col>
            <Col xs={12}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Type Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={quickTypeName}
                onChange={(event) => setQuickTypeName(event.target.value)}
                placeholder="Enter Type Name"
                style={commonInputStyle}
                autoFocus
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton
            onClick={() => setShowQuickTypeModal(false)}
            disabled={isQuickTypeSaving}
            showConfirmation={false}
          />
          <PrimarySubmitButton
            type="button"
            label="Add Type"
            isLoading={isQuickTypeSaving}
            loadingLabel="Adding..."
            onClick={handleQuickAddType}
          />
        </Modal.Footer>
      </Modal>

      <Modal
        show={showQuickModelModal}
        onHide={() => !isQuickModelSaving && setShowQuickModelModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bright-white-close"
          style={{
            backgroundColor: MecordColors.ThemeBGColor,
            paddingBottom: "12px",
          }}
        >
          <Modal.Title style={{ color: "#fff" }}>Add Model</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Make
              </Form.Label>
              <Form.Control
                value={
                  makes.find((make) => make.make_id === formik.values.make)
                    ?.make_name || ""
                }
                readOnly
                style={commonInputStyle}
              />
            </Col>
            <Col xs={12}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Model Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={quickModelName}
                onChange={(event) => setQuickModelName(event.target.value)}
                placeholder="Enter Model Name"
                style={commonInputStyle}
                autoFocus
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton
            onClick={() => setShowQuickModelModal(false)}
            disabled={isQuickModelSaving}
            showConfirmation={false}
          />
          <PrimarySubmitButton
            type="button"
            label="Add Model"
            isLoading={isQuickModelSaving}
            loadingLabel="Adding..."
            onClick={handleQuickAddModel}
          />
        </Modal.Footer>
      </Modal>
      {/* Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
        <CancelButton
          onClick={() => {
            if (onCancel) {
              onCancel();
            }
          }}
          disabled={isSubmitting}
          showConfirmation={false}
        />
        <PrimarySubmitButton
          label="Add"
          isLoading={isSubmitting}
          loadingLabel="Adding..."
        />
      </div>
    </Form>
  );
};

export default ProductFormModal;



